import { Localization } from "@viamap/viamap2-common";
import { ADModalBody, ADModalFooter, ADModalInfo, AdvancedDragModal } from "src/componentsUtils/AdvancedDragModal";
import { closeWindow, ManagedWindow, openWindow, WindowId } from "src/WindowManager/WindowState";
import { GlassInputGroup, GlassTextInputTransformer } from "./GlassInput";
import { useContext, useEffect, useState } from "react";
import { MapitStateContext } from "src/states/MapitState";
import { GlassCheckbox } from "./MitGlassComponents";
import { useWindowContext } from "src/WindowManager/useWindowContext";
import { addPreviewLayer, removePreviewLayer } from "src/utils/mapLayerHelpers";
import { GeoJSONSource } from "maplibre-gl";
import { buffer, center, centerOfMass, centroid, circle, clone, combine, concave, convex, difference, dissolve, envelope, explode, featureCollection, flatten, intersect, kinks, lineOffset, lineToPolygon, polygonize, polygonSmooth, polygonToLine, simplify, union, voronoi } from "@turf/turf";
import { Persistence } from "src/managers/Persistence";
import { LayerInfo } from "src/common/managers/Types";
import { Typeahead } from "react-bootstrap-typeahead";

export function LayerFunctions(props: { window: ManagedWindow }) {
   const { state: mapitState, dispatch: mapitDispatch } = useContext(MapitStateContext);
   const { dispatch: windowDispatch } = useWindowContext()
   const [preview, setPreview] = useState(true);
   const [selectedFunc, setSelectedFunc] = useState<string>(Object.keys(funList)[0]);
   const [selectedLayer, setSelectedLayer] = useState<number[]>([]);

   useEffect(() => {
      if (selectedLayer.length === 0) return;
      if (preview) {
         const previewID = "layerFunPreview";
         let map = mapitState.map?.getMapPop();
         if (map.getSource(previewID)) {
            removePreviewLayer(map, previewID);
         }
         addPreviewLayer(map!, previewID, "purple", "pink");
         let features = retriveSelectedFeatures(selectedLayer, mapitState.layers);
         let funData = funList[selectedFunc].func(featureCollection(features));
         if (funData) {
            (map.getSource(previewID) as GeoJSONSource)?.setData(funData as any);
         }
         return () => {
            removePreviewLayer(map!, previewID);
         }
      }
   }, [selectedLayer, preview, selectedFunc])

   let usableLayers = Object.values(mapitState.layers)

   return (
      <AdvancedDragModal window={props.window} title={Localization.getText("Layer Functions")}
         onClose={() => { windowDispatch(closeWindow(props.window.id)) }}
         PosDefault={{ width: "360px", height: "600px", left: "20px", top: "55px" }} >
         <ADModalInfo>
            {Localization.getText("Newly created geometries will not include any attributes from the source layer.")}
         </ADModalInfo>
         <ADModalBody>
            <GlassInputGroup>
               <GlassTextInputTransformer label={Localization.getText("Function")} >
                  <select value={selectedFunc} onChange={(e) => setSelectedFunc(e.target.value)}>
                     {Object.entries(funList).map(([key, value]) => {
                        let lang = Localization.getLanguage();
                        return <option key={key} value={key}>{value[lang]}</option>
                     })}
                  </select>
               </GlassTextInputTransformer>
               <GlassTextInputTransformer label={Localization.getText("Layer")} >
                  <Typeahead
                     multiple={true}
                     id="layerTypeahead"
                     options={usableLayers.map((a) => ({ id: a.layerId, datasetname: a.datasetname }))}
                     labelKey={"datasetname"}
                     placeholder={Localization.getText("please select") + " " + Localization.getText("layer")}
                     emptyLabel={Localization.getText("No results found")}
                     onChange={(selected) => setSelectedLayer(selected.map((a: any) => a.id))}
                     selected={usableLayers.map((a) => ({ id: a.layerId, datasetname: a.datasetname })).filter((a) => selectedLayer.includes(a.id))}
                     positionFixed={true}
                  />
               </GlassTextInputTransformer>

            </GlassInputGroup>
         </ADModalBody>
         <ADModalFooter>
            <GlassCheckbox checked={preview} onChange={() => setPreview(!preview)} >{Localization.getText("Preview")}</GlassCheckbox>
            <button onClick={async (e) => {
               let allFeatures = retriveSelectedFeatures(selectedLayer, mapitState.layers);
               let datafun = applyFunction(selectedFunc, featureCollection(allFeatures));
               if (!datafun) return;
               let layerInfo = await new Promise<LayerInfo>((res) => {
                  Persistence.createLayersFromGeoJSON(
                     applyFunName(selectedFunc, mapitState.layers[selectedLayer[0]].datasetname),
                     datafun,
                     new Date(),
                     (layer) => res(layer)
                  )
               });
               windowDispatch(openWindow(
                  WindowId.NameOnCreation,
                  { layers: layerInfo }
               ))
            }} className="GlassButton">{Localization.getText("Create layer")}</button>
         </ADModalFooter>
      </AdvancedDragModal>
   )
}

function applyFunction(func: string, geojson: any) {
   return funList[func].func(geojson);
}

function applyFunName(func: string, label: string) {
   let lang = Localization.getLanguage();
   return funList[func][lang] + ": " + label;
}

function retriveSelectedFeatures(selectedLayer: number[], layers: { [id: number]: LayerInfo }) {
   let features = selectedLayer.map(l => {
      let layer = layers[l];
      if (layer && layer.geoJson) {
         return layer.geoJson.features;
      }
      return [];
   }).flat();
   return features;
}

const funList = {
   "group": { "type": "any", da: "Grupper", en: "Group", func: (ft) => ft },
   "combine": { "type": "any", da: "Kombiner", en: "Combine", func: (ft) => combine(ft) },
   // "explode": { "type": "any", da: "Eksploder", en: "Explode", func: (ft) => explode(ft) }, -- ved ikke hvad folks skal bruge den til 
   "envelope": { "type": "any", da: "Konvolut", en: "Envelope", func: (ft) => envelope(ft) },
   "center": { "type": "any", da: "Center", en: "Center", func: (ft) => center(ft) },
   "centerOfMass": { "type": "any", da: "Tyngdepunkt", en: "Center of Mass", func: (ft) => centerOfMass(ft) },
   "centroid": { "type": "any", da: "Centroid", en: "Centroid", func: (ft) => centroid(ft) },
   "flatten": { "type": "any", da: "Flad", en: "Flatten", func: (ft) => flatten(ft) },

   // --- Følgende kan aktiveres når der er UI nok til at håndtere ekstra parametre ---

   // "simplify": { "type": "any", da: "Forenkle", en: "Simplify", func: (ft) => simplify(ft, { tolerance: 0.01 }) },
   // "buffer": { "type": "any", da: "Buffer", en: "Buffer", func: (ft) => buffer(ft, 100) },

   // --- Flere kan aktiveres når der er håndteres features i forskellige typer (point, line, polygon) ---

   // "area": {  "type": "polygon",  },
   // "distance": { "type": "point_2", da: "Afstand", en: "Distance", func: (ft) => ft },
   // "length": { "type": "line|polygon", da: "Længde", en: "Length", func: (ft) => ft },
   // "bboxClip": { "type": "line|polygon", da: "BBox Clip", en: "BBox Clip", func: (ft) => bboxClip(ft, [0, 0, 10, 10]) },
   // "circle": { "type": "point", da: "Cirkel", en: "Circle", func: (ft) => circle(ft, 100) },
   // "clone": { "type": "any", da: "Klon", en: "Clone", func: (ft) => clone(ft) },
   // "concave": { "type": "points", da: "Konkav", en: "Concave", func: (ft) => concave(ft) },
   // "convex": { "type": "points", da: "Konveks", en: "Convex", func: (ft) => convex(ft) },
   // "difference": { "type": "polygon", da: "Forskel", en: "Difference", func: (ft) => difference(ft) },
   // "dissolve": { "type": "polygon", da: "Opløs", en: "Dissolve", func: (ft) => dissolve(ft) },
   // "intersect": { "type": "polygon", da: "Skæringspunkt", en: "Intersect", func: (ft) => intersect(ft) },
   // "lineOffset": { "type": "line", da: "Linjeoffset", en: "Line Offset", func: (ft) => lineOffset(ft, 100) },
   // "polygonSmooth": { "type": "polygon", da: "Polygon Glat", en: "Polygon Smooth", func: (ft) => polygonSmooth(ft) },
   // "union": { "type": "polygon", da: "Union", en: "Union", func: (ft) => union(ft) },
   // "voronoi": { "type": "points", da: "Voronoi", en: "Voronoi", func: (ft) => voronoi(ft) },
   // "lineToPolygon": { "type": "line", da: "Linje til Polygon", en: "Line to Polygon", func: (ft) => lineToPolygon(ft) },
   // "polygonToLine": { "type": "polygon", da: "Polygon til Linje", en: "Polygon to Line", func: (ft) => polygonToLine(ft) },
   // "polygonize": { "type": "line", da: "Polygonize", en: "Polygonize", func: (ft) => polygonize(ft) },
   // "kinks": { "type": "line|polygon", da: "Kinks", en: "Kinks", func: (ft) => kinks(ft) },
}